import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useEffect, useState } from "react";
import L from "leaflet";

const moduleStyleMap: Record<
  string,
  { color: string; icon: string; label: string }
> = {
  CHARTER_AVAILABLE: {
    color: "#2563EB",
    icon: "✈",
    label: "Charter Available",
  },
  CHARTER_WANTED: {
    color: "#2563EB",
    icon: "✈",
    label: "Charter Wanted",
  },

  WET_LEASE_AVAILABLE: {
    color: "#EA580C",
    icon: "✈",
    label: "Wet Lease Available",
  },
  WET_LEASE_WANTED: {
    color: "#EA580C",
    icon: "✈",
    label: "Wet Lease Wanted",
  },
  WET_LEASE_AOG: {
    color: "#EA580C",
    icon: "⚠",
    label: "Wet Lease AOG",
  },

  AIRCRAFT_SALE_LEASE_AVAILABLE: {
    color: "#16A34A",
    icon: "✈",
    label: "Aircraft Sale / Lease Available",
  },
  AIRCRAFT_SALE_LEASE_WANTED: {
    color: "#16A34A",
    icon: "✈",
    label: "Aircraft Sale / Lease Wanted",
  },

  BUSINESS_JET_AVAILABLE: {
    color: "#9333EA",
    icon: "✈",
    label: "Business Jet",
  },

  ENGINE_SALE_LEASE_AVAILABLE: {
    color: "#DC2626",
    icon: "⚙",
    label: "Engine Sale / Lease",
  },
};

const geocodeCache = new Map<string, [number, number]>();

const geocode = async (query: string): Promise<[number, number] | null> => {
  if (geocodeCache.has(query)) return geocodeCache.get(query)!;

  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      query,
    )}`,
  );

  const json = await res.json();
  if (!json?.length) return null;

  const coords: [number, number] = [
    parseFloat(json[0].lat),
    parseFloat(json[0].lon),
  ];

  geocodeCache.set(query, coords);
  return coords;
};

const FixResize = () => {
  const map = useMap();
  useEffect(() => {
    requestAnimationFrame(() => map.invalidateSize());
  }, [map]);
  return null;
};

export const AircraftMap = ({ data }: { data: any[] }) => {
  const [markers, setMarkers] = useState<any[]>([]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const results: any[] = [];
      const toGeocode: any[] = [];

      // 1. Process items with coordinates immediately
      data.forEach((x, index) => {
        const lat = parseFloat(x.latitude || x.location?.latitude);
        const lng = parseFloat(x.longitude || x.langitude || x.location?.longitude || x.location?.langitude);
        
        // Offset logic for visual separation of overlapping markers
        const offset = (index % 6) * 0.02;

        if (!isNaN(lat) && !isNaN(lng)) {
          const module = moduleStyleMap[x.moduleName];
          if (module) {
            results.push({
              id: `${x.moduleName}_${x.id}`,
              position: [lat + offset, lng + offset],
              headline: x.headline,
              registration: x.registration,
              company: x.companyName,
              module,
            });
          }
        } else {
          // If no coords, queue for geocoding
          toGeocode.push({ x, index });
        }
      });

      // Update state with immediate results first to be "fast"
      if (isMounted && results.length > 0) {
        setMarkers([...results]);
      }

      // 2. Process geocoding queue sequentially with delay to be "safe" (avoid 429)
      for (const item of toGeocode) {
        if (!isMounted) break;
        const { x, index } = item;
        const query = x.cityName || x.countryName;
        
        if (query) {
           try {
             const coords = await geocode(query);
             // Rate limit delay (1s) to respect Nominatim TOS
             await new Promise(r => setTimeout(r, 1000));
             
             if (coords && isMounted) {
               const module = moduleStyleMap[x.moduleName];
               if (module) {
                 const offset = (index % 6) * 0.02;
                 const newMarker = {
                    id: `${x.moduleName}_${x.id}`,
                    position: [coords[0] + offset, coords[1] + offset],
                    headline: x.headline,
                    registration: x.registration,
                    company: x.companyName,
                    module,
                 };
                 // Append new marker
                 setMarkers(prev => [...prev, newMarker]);
               }
             }
           } catch (e) {
             console.warn("Geocoding failed for", query);
           }
        }
      }
    };

    run();

    return () => { isMounted = false; };
  }, [data]);

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <MapContainer
        center={[39, 35]}
        zoom={5}
        minZoom={3}
        maxZoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        dragging
      >
        <FixResize />

        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {markers.map((m) => (
          <Marker
            key={m.id}
            position={m.position}
            icon={
              new L.DivIcon({
                className: "",
                html: `
                  <div style="
                    width:28px;
                    height:28px;
                    background:${m.module.color};
                    border-radius:50%;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    color:white;
                    font-size:14px;
                    box-shadow:0 0 6px rgba(0,0,0,.4);
                  ">${m.module.icon}</div>
                `,
                iconSize: [28, 28],
                iconAnchor: [14, 14],
              })
            }
          >
            <Popup>
              <strong>{m.headline}</strong>
              <br />
              {m.registration}
              <br />
              {m.company}
              <br />
              {m.module.label}
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* LEGEND */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          background: "white",
          padding: 10,
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,.2)",
          fontSize: 12,
          zIndex: 1000,
        }}
      >
        {Object.values(moduleStyleMap).map((m) => (
          <div
            key={m.label}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: m.color,
              }}
            />
            {m.label}
          </div>
        ))}
      </div>
    </div>
  );
};
